import React, { useEffect, useState } from 'react';
import ContentHeader from './ContentHeader';
import {
  ContentLink,
  Typography,
  VerticalTabs,
} from 'br2s-ui-componentlibrary_r2_02';
import { SystemIcon } from 'br2s-ui-componentlibrary_r2_02/dist/foundation/assets/system-icon';
import DropdownLink from './DropdownLink';
import './MegaMenuContent.scss';
import classNames from 'classnames';
import { Transaction } from './megaMenuConstants/transactions';
import {
  TMegaMenuContent,
  TabsContent,
  L2Persona,
  L1Persona,
  PlainContent,
} from './megaMenuConstants/content';
import useNavigateGuard from '@/utilities/hooks/useNavigateGuard';

export type MegaMenuContentProps = {
  data: TMegaMenuContent;
  isDropdownOpen: boolean;
  closeDropdown: (t?: boolean) => void;
};

const MegaMenuContent = ({
  data: contentData,
  closeDropdown,
}: MegaMenuContentProps) => {
  const guardedNavigate = useNavigateGuard();
  const [activeTab, setActiveTab] = useState(0);
  const [L1Tab, setL1Tab] = useState<number | null>(null);
  const [L2Tab, setL2Tab] = useState<number | null>(null);

  const isL2Tabs = ['Manage', 'Annual filing', 'Deregister'].includes(
    contentData.heading,
  );
  const isL1Tabs = ['Register', 'Buy information'].includes(
    contentData.heading,
  );

  useEffect(() => {
    if (L1Tab !== null) setActiveTab(L1Tab);
  }, [L1Tab]);

  const data = contentData as TabsContent;

  const tabs = data.personas?.map(({ id, label }) => ({
    id,
    label,
    onClick: () => setActiveTab(id),
  }));

  const navigateURL = (url: string) => {
    guardedNavigate(url);
    closeDropdown(true);
  };

  const getContentHeaderProps = () => {
    const getPropsForL2SubTab = () => ({
      title: (data.personas[L1Tab as number] as L2Persona).headers[
        L2Tab as number
      ].label,
      backButtonLabel: data.personas[L1Tab as number].label,
      onBackClick: () => setL2Tab(null),
    });

    const getPropsForL1Tab = () => ({
      title: data.personas[L1Tab as number].label,
      backButtonLabel: contentData.heading,
      onBackClick: () => setL1Tab(null),
    });

    const props = {
      title: contentData.heading,
      description: contentData.description,
      backButtonLabel: 'Main menu',
      onBackClick: () => closeDropdown(false),
    };

    if (isL2Tabs && L1Tab !== null && L2Tab !== null) {
      return { ...props, ...getPropsForL2SubTab() };
    }

    if (L1Tab !== null) {
      return { ...props, ...getPropsForL1Tab() };
    }

    return props;
  };

  const openLink = ({
    href,
    deprioHref,
    newTab,
    deprioFlag,
  }: Pick<Transaction, 'href' | 'deprioHref' | 'newTab' | 'deprioFlag'>) => {
    const path = deprioHref && deprioFlag !== 'true' ? deprioHref : href;

    if (newTab) {
      window.open(path, '_blank');
      return false;
    }

    navigateURL(path);
  };
  const renderContentData = () => {
    if ((data.personas[activeTab] as L2Persona).headers) {
      return (data.personas[activeTab] as L2Persona).headers.map(
        ({ id, label, transactions }) => {
          return (
            <div key={id}>
              <Typography type="headline-5" weight="bold" label={label} />
              <div className="row">
                {transactions.map(({ id, label, icon, ...transaction }) => (
                  <div className="col-lg-6" key={id}>
                    <ContentLink
                      key={id}
                      id={id}
                      label={label}
                      iconClass={icon ?? SystemIcon.ActionAdd}
                      tag="button"
                      onClick={() => openLink(transaction)}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        },
      );
    }

    return (
      <div className="row">
        {(data.personas[activeTab] as L1Persona).transactions.map(
          ({ id, label, icon, ...transaction }) => (
            <div className="col-lg-6" key={id}>
              <ContentLink
                id={id}
                label={label}
                iconClass={icon ?? SystemIcon.ActionAdd}
                tag="button"
                onClick={() => openLink(transaction)}
              />
            </div>
          ),
        )}
      </div>
    );
  };

  const renderMobileContent = () => {
    const getTransactionContent = (transactions: Transaction[]) =>
      transactions?.map(({ id, label, icon, ...transaction }) => (
        <ContentLink
          key={id}
          id={id}
          label={label}
          iconClass={icon ?? SystemIcon.ActionAdd}
          tag="button"
          onClick={() => openLink(transaction)}
        />
      ));

    if (data.personas === undefined) {
      return getTransactionContent((contentData as PlainContent).transactions);
    }

    const activePersona = data.personas[activeTab] as L2Persona;

    if (
      activePersona.headers !== undefined &&
      L1Tab !== null &&
      L2Tab !== null
    ) {
      return getTransactionContent(activePersona.headers[L2Tab].transactions);
    }

    if (activePersona.headers !== undefined && L1Tab !== null) {
      return (data.personas[L1Tab] as L2Persona).headers?.map(
        (header, index) => (
          <DropdownLink
            key={header.id}
            onClick={() => setL2Tab(index)}
            id={header.id}
            label={header.label}
          />
        ),
      );
    }

    if (activePersona.headers === undefined && L1Tab !== null) {
      return getTransactionContent(
        (data.personas[L1Tab] as L1Persona).transactions,
      );
    }

    return data.personas.map((persona, index: number) => (
      <DropdownLink
        key={persona.id}
        onClick={() => setL1Tab(index)}
        id={persona.label}
        label={persona.label}
      />
    ));
  };

  return (
    <div
      className={classNames('mega-menu-main-content', {
        'bg-white': !isL1Tabs && !isL2Tabs,
      })}
    >
      <ContentHeader
        {...getContentHeaderProps()}
        overviewHref={contentData.overviewHref}
        closeDropdown={closeDropdown}
      />

      <div className="container content-wrapper">
        {isL1Tabs || isL2Tabs ? (
          <VerticalTabs
            id="mega-menu-vertical-tabs"
            activeIndex={activeTab}
            tabs={tabs}
            isHaveBorder={false}
          >
            <div className="mega-menu-content">
              <div className="content-subheader">
                <Typography
                  type="headline-5"
                  weight="bold"
                  label={data.personas[activeTab].label}
                />
                <Typography
                  type="base"
                  label={data.personas[activeTab].description}
                />
              </div>
              <div className="content-links row">{renderContentData()}</div>
            </div>
          </VerticalTabs>
        ) : (
          <div className="container row plain-content">
            {(contentData as PlainContent).transactions.map(
              ({ id, label, icon, ...transaction }) => (
                <ContentLink
                  key={id}
                  id={id}
                  label={label}
                  iconClass={icon ?? SystemIcon.ActionAdd}
                  tag="button"
                  onClick={() => openLink(transaction)}
                />
              ),
            )}
          </div>
        )}
      </div>

      <div className="container mobile-wrapper row">
        {renderMobileContent()}
      </div>
    </div>
  );
};

export default MegaMenuContent;
